import { cloneElement, forwardRef, isValidElement } from 'react';

import type { ReactElement, Ref, SVGAttributes } from 'react';

import type { IconProps } from './icon.types';

/**
 * This is a wrapper component to wrap any SVG icon and provide a unified interface
 * to manipulate its props
 *
 * ### Usage
 * #### Library Icons
 * ```
 * import { FireIcon } from '@bloomreach/react-banana-ui'
 *
 * <FireIcon />
 * ```
 *
 * #### Custom Icons
 * ```
 * import Icon from '@bloomreach/react-banana-ui'
 * import CustomSvgIcon from 'path/to/icon.svg?react'
 *
 * <Icon size={100} id="some-id" className="some-class" aria-label="some description">
 *   <CustomSvgIcon />
 * </Icon>
 * ```
 */

const Icon = forwardRef((props: IconProps, ref: Ref<SVGSVGElement>) => {
  const { children, color, size, ...rest } = props;

  const svgProps = {
    ...(size ? { height: size, width: size } : {}),
    ...(color ? { fill: color } : {}),
    ref,
    ...rest,
  };

  if (isValidElement(children)) {
    return cloneElement(children as ReactElement<SVGAttributes<SVGElement>>, svgProps);
  }

  return null;
});

Icon.displayName = 'Icon';

export default Icon;
